import { useCallback, useState } from "react";
import { GridRowSelectionModel } from "@mui/x-data-grid-pro";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useCreateDebounceHandler, useDebouncedCallback } from "../../../../common/hooks/use_debounced_callback";
import { processError } from "../../../../common/utils/base_error";
import { evaluateParamValue } from "../../../../common/utils/params_utils";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { AvaliacaoTreinamento } from "../../../rh/domain/models/avaliacao_treinamento";
import { AvaliarTreinamentoForm } from "../../../rh/presentation/schemas/Funcionarios_cadastro/avaliar_treinamento_schema";
import { useAvaliacaoTreinamentoLoteTercCadastroFilters } from "../../../rh/presentation/hooks/AvaliacaoTreinamentoLote_cadastroTerc/filters";
import { useAvaliacaoTreinamentoTercLoteCadastroPagination } from "../../../rh/presentation/hooks/AvaliacaoTreinamentoLote_cadastroTerc/pagination";

/**
 * LoadAvaliacaoTreinamentoLoteState
 */

type LoadAvaliacaoTreinamentoLoteState = {
  idCargo?: number | null;
  idFuncionario?: number | null;
  idCentroCustos?: number[] | null;
  vencimentoDe?: Date | null;
  vencimentoAte?: Date | null;
  page?: number;
  rowsPerPage?: number;
};

/**
 * useAvaliacaoTreinamentoLoteCadastroViewModel
 */

export const useAvaliacaoTreinamentoLoteCadastroViewModel = () => {
  const {
    commons: {
      getCentroCustoDropDownUseCase,
      getCargosDropDownUseCase,
      getFuncionariosTercDropDownUseCase,
    },
    terceirizacao: {
      avaliarTreinamentoUseCase,
      getAllAvaliacaoTreinamentoLoteUseCase,
      
    }
  } = useDependencyInjection();

  const debouncedRef = useCreateDebounceHandler(750);

  const { pushErrorNotification, pushInfoNotification } = usePageLoader();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const [funcionarioTreinamentos, setFuncionarioTreinamentos] = useState<AvaliacaoTreinamento[]>();

  const [openAvaliarFuncionarioTreinamentoModal, setOpenAvaliarFuncionarioTreinamentoModal] = useState(false);

  const [rowsSelectionModel, setRowsSelectionModel] =  useState<GridRowSelectionModel>();

  const filters = useAvaliacaoTreinamentoLoteTercCadastroFilters();
  const pagination = useAvaliacaoTreinamentoTercLoteCadastroPagination();

  /**
   * Funcoes de carregamento
   */

  const loadCentroCustoDropDown = useCallback(async () => {
    try {
      const centroCustos = await getCentroCustoDropDownUseCase.getCentroCustoDropDown();
      useAvaliacaoTreinamentoLoteTercCadastroFilters.setState({ centroCustos });
    } catch (e: unknown) {
      useAvaliacaoTreinamentoLoteTercCadastroFilters.setState({ centroCustos: [] });
      throw e;
    }
  }, [
    getCentroCustoDropDownUseCase,
  ]);

  const loadFuncionariosDropDown = useCallback(async () => {
    try {
      const funcionarios = await getFuncionariosTercDropDownUseCase.getFuncionariosTercDropDown();
      useAvaliacaoTreinamentoLoteTercCadastroFilters.setState({ funcionarios });
    } catch (e: unknown) {
      useAvaliacaoTreinamentoLoteTercCadastroFilters.setState({ funcionarios: [] });
      throw e;
    }
  }, [getFuncionariosTercDropDownUseCase]);

  const loadCargosDropDown = useCallback(async () => {
    try {
      const cargos = await getCargosDropDownUseCase.getCargosDropDown();
      useAvaliacaoTreinamentoLoteTercCadastroFilters.setState({ cargos });
    } catch (e: unknown) {
      useAvaliacaoTreinamentoLoteTercCadastroFilters.setState({ cargos: [] });
      throw e;
    }
  }, [ getCargosDropDownUseCase]);

  const loadAvaliacaoTreinamentoLote = useCallback(async (funcState?: LoadAvaliacaoTreinamentoLoteState) => {
    try {
      const filteredIdCargo = evaluateParamValue(funcState?.idCargo, filters.selectedIdCargo);
      const filteredIdFuncionario = evaluateParamValue(funcState?.idFuncionario, filters.selectedIdFuncionario);
      const filteredIdCentroCusto = evaluateParamValue(funcState?.idCentroCustos, filters.selectedIdCentroCusto);
      const filteredVencimentoDe = evaluateParamValue(funcState?.vencimentoDe, filters.vencimentoDe);
      const filteredVencimentoAte = evaluateParamValue(funcState?.vencimentoAte, filters.vencimentoAte);

      const filteredPage = funcState?.page ?? pagination.page;
      const filteredRowsPerPage = funcState?.rowsPerPage ?? pagination.rowsPerPage;

      await getAllAvaliacaoTreinamentoLoteUseCase.getAllAvaliacaoTreinamentoLote(
        filteredIdCargo,
        filteredIdFuncionario,
        filteredIdCentroCusto,
        filteredVencimentoDe,
        filteredVencimentoAte,
        filteredPage,
        filteredRowsPerPage,
      );

      setFuncionarioTreinamentos(getAllAvaliacaoTreinamentoLoteUseCase.items);

      pagination.setTotalItems(getAllAvaliacaoTreinamentoLoteUseCase.totalItems);
    } catch (e: unknown) {
      setFuncionarioTreinamentos([]);
      throw e;
    }
  }, [filters.selectedIdCargo, filters.selectedIdCentroCusto, filters.selectedIdFuncionario, filters.vencimentoAte, filters.vencimentoDe, getAllAvaliacaoTreinamentoLoteUseCase, pagination]);

  /**
   * useEffect
   */

  usePageStartBootstrap(async () => {
    try {
      setErrorMessage(undefined);
      await loadCentroCustoDropDown();
      await loadFuncionariosDropDown();
      await loadCargosDropDown();
      await loadAvaliacaoTreinamentoLote();
    } catch (e: unknown) {
      setErrorMessage(processError(e, "Falha ao buscar assinaturas!")[0]);
    }
  });

  /**
   * Eventos
   */

  const handleSubmitAvaliarTreinamento = useCallback(async (form: AvaliarTreinamentoForm) => {
    try {
      if (!saving && !loading) {
        setSaving(true);

        if (funcionarioTreinamentos && rowsSelectionModel && rowsSelectionModel.ids.size > 0) {
          const idFuncionarios: number[] = [];
          for (let row of rowsSelectionModel.ids.values()) {
            if (typeof row === 'number') {
              idFuncionarios.push(row);
            } else if (typeof row === 'string') {
              try {
                idFuncionarios.push(Number(row));
              } catch (e) {
              }
            }
          }
          await avaliarTreinamentoUseCase.avaliarTreinamentoLote(
            idFuncionarios,
            form.eficaz,
            form.avaliador,
            form.dataAvaliacao,
            form.evidenciaObjetiva,
          );

          await loadAvaliacaoTreinamentoLote();

          pushInfoNotification("Sucesso", "Alterações salvas com sucesso!", { isAutoFinish: true });
        }
      }
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, "Falha ao salvar documento!"));
    } finally {
      setSaving(false);
      setOpenAvaliarFuncionarioTreinamentoModal(false);
    }
  }, [saving, loading, funcionarioTreinamentos, rowsSelectionModel, avaliarTreinamentoUseCase, loadAvaliacaoTreinamentoLote, pushInfoNotification, pushErrorNotification]);

  const handleReloadPage = useCallback(async () => {
    try {
      setLoading(true);

      await loadCentroCustoDropDown();
      await loadFuncionariosDropDown();
      await loadCargosDropDown();
      await loadAvaliacaoTreinamentoLote();
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, "Falha ao buscar dados!"));
    } finally {
      setLoading(false);
    }
  }, [
    loadCentroCustoDropDown,
    loadFuncionariosDropDown,
    loadCargosDropDown,
    loadAvaliacaoTreinamentoLote,
    pushErrorNotification,
  ]);

  const handleRowsSelectionChange = useCallback((rows:GridRowSelectionModel) => {
    setRowsSelectionModel(rows);
  }, []);

  const handlePageChange = useCallback(async (page: number) => {
    try {
      setLoading(true);
      useAvaliacaoTreinamentoTercLoteCadastroPagination.setState({ page: page + 1 });
      await loadAvaliacaoTreinamentoLote({ page: page + 1 });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao buscar dados!"));
    } finally {
      setLoading(false);
    }
  }, [
    loadAvaliacaoTreinamentoLote,
    pushErrorNotification,
  ]);

  const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
    try {
      setLoading(true);
      useAvaliacaoTreinamentoTercLoteCadastroPagination.setState({ page: 1, rowsPerPage });
      await loadAvaliacaoTreinamentoLote({ page: 1, rowsPerPage });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao buscar dados!"));
    } finally {
      setLoading(false);
    }
  }, [
    loadAvaliacaoTreinamentoLote,
    pushErrorNotification,
  ]);

  const handleSelectedCargoChange = useDebouncedCallback(debouncedRef, (idCargo: number | null) => {
    useAvaliacaoTreinamentoLoteTercCadastroFilters.setState({ selectedIdCargo: idCargo });
    return async () => {
      try {
        setLoading(true);
        await loadAvaliacaoTreinamentoLote({ idCargo });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao buscar dados!"));
      } finally {
        setLoading(false);
      }
    };
  }, [
    loadAvaliacaoTreinamentoLote,
    pushErrorNotification,
  ]);

  const handleSelectedFuncionarioChange = useDebouncedCallback(debouncedRef, (idFuncionario: number | null) => {
    useAvaliacaoTreinamentoLoteTercCadastroFilters.setState({ selectedIdFuncionario: idFuncionario });
    return async () => {
      try {
        setLoading(true);
        await loadAvaliacaoTreinamentoLote({ idFuncionario });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao buscar dados!"));
      } finally {
        setLoading(false);
      }
    };
  }, [
    loadAvaliacaoTreinamentoLote,
    pushErrorNotification,
  ]);

  const handleSelectedCentroCustoChange = useDebouncedCallback(debouncedRef, (idCentroCustos: number[] | null) => {
    useAvaliacaoTreinamentoLoteTercCadastroFilters.setState({ selectedIdCentroCusto: idCentroCustos });
    return async () => {
      try {
        setLoading(true);
        await loadAvaliacaoTreinamentoLote({ idCentroCustos });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao buscar dados!"));
      } finally {
        setLoading(false);
      }
    };
  }, [
    loadAvaliacaoTreinamentoLote,
    pushErrorNotification,
  ]);

  const handleVencimentoChange = useDebouncedCallback(debouncedRef, (de: Date | null, ate: Date | null) => {
    useAvaliacaoTreinamentoLoteTercCadastroFilters.setState({ vencimentoDe: de, vencimentoAte: ate });
    return async () => {
      try {
        setLoading(true);
        await loadAvaliacaoTreinamentoLote({ vencimentoDe: de, vencimentoAte: ate });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao buscar dados!"));
      } finally {
        setLoading(false);
      }
    };
  }, [
    loadAvaliacaoTreinamentoLote,
    pushErrorNotification,
  ]);

  const handleClear = useCallback(async () => {
    try {
      setLoading(true);

      useAvaliacaoTreinamentoLoteTercCadastroFilters.getState().clear();

      await loadAvaliacaoTreinamentoLote({
        idCargo: null,
        idCentroCustos: null,
        idFuncionario: null,
        vencimentoAte: null,
        vencimentoDe: null,
      });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao buscar dados!"));
    } finally {
      setLoading(false);
    }
  }, [
    loadAvaliacaoTreinamentoLote,
    pushErrorNotification,
  ]);

  const handleOpenAvaliarFuncionarioTreinamentoModal = useCallback((openState: boolean) => {
    setOpenAvaliarFuncionarioTreinamentoModal(openState);
  }, []);

  return {
    loading,
    saving,
    errorMessage,
    funcionarioTreinamentos: funcionarioTreinamentos ?? [],
    openAvaliarFuncionarioTreinamentoModal,
    rowsSelectionModel,
    filters,
    pagination,
    setRowsSelectionModel,
    handleSubmitAvaliarTreinamento,
    handleReloadPage,
    handleRowsSelectionChange,
    handlePageChange,
    handleRowsPerPageChange,
    handleSelectedCargoChange,
    handleSelectedFuncionarioChange,
    handleSelectedCentroCustoChange,
    handleVencimentoChange,
    handleClear,
    handleOpenAvaliarFuncionarioTreinamentoModal,
  };
}